import React, { useState, useRef, useEffect } from 'react';
import { useEditMode } from './EditModeProvider';
import { Play, Square, MousePointer2, Pointer } from 'lucide-react';

const EditableAudio = ({ contentKey, defaultSrc, label = "прослушать демо" }) => {
  const { getContent } = useEditMode();
  const currentSrc = getContent(contentKey, defaultSrc);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // --- Логика подсказки (курсора) ---
  const [isHintVisible, setIsHintVisible] = useState(true);
  const [cursorState, setCursorState] = useState('hidden'); // 'hidden', 'visible', 'clicking'
  const [isMobile, setIsMobile] = useState(false);
  const playCount = useRef(0);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isHintVisible) return;

    let isMounted = true;
    let intervalId;
    let timeouts = [];

    const runSequence = () => {
      if (!isMounted) return;
      if (playCount.current >= 3) {
        setIsHintVisible(false);
        return;
      }

      setCursorState('visible');
      
      // Движение к кнопке
      timeouts.push(setTimeout(() => { if (isMounted) setCursorState('clicking'); }, 600));
      // Имитация нажатия (уменьшение масштаба)
      timeouts.push(setTimeout(() => { if (isMounted) setCursorState('visible'); }, 900));
      // Отведение курсора и скрытие
      timeouts.push(setTimeout(() => { 
        if (isMounted) {
          setCursorState('hidden');
          playCount.current += 1;
        }
      }, 1500));
    };

    // Первый запуск с небольшой задержкой
    timeouts.push(setTimeout(runSequence, 1000));
    // Последующие повторения каждые 5 секунд
    intervalId = setInterval(runSequence, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      timeouts.forEach(clearTimeout);
    };
  }, [isHintVisible]);
  // ----------------------------------

  const togglePlay = () => {
    setIsHintVisible(false); // Скрываем подсказку при любом взаимодействии
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(e => console.error("Audio play failed:", e));
      setIsPlaying(true);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (audioRef.current) {
        audioRef.current.currentTime = 0;
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <audio 
        ref={audioRef} 
        src={currentSrc} 
        loop={false} 
        onEnded={handleEnded} 
      />
      
      <div className="relative flex items-center justify-center shrink-0">
        {/* Анимация расходящегося круга при "клике" подсказки */}
        {isHintVisible && cursorState === 'clicking' && (
            <div className="absolute inset-0 rounded-full bg-[var(--tab-active-bg,#00797A)] animate-ping opacity-60 pointer-events-none"></div>
        )}
        
        <button 
          onClick={togglePlay}
          className={`flex items-center justify-center w-7 h-7 rounded-full bg-[var(--tab-active-bg,#00797A)] hover:opacity-80 text-white transition-all z-10 ${isHintVisible ? 'shadow-[0_0_0_2px_rgba(0,121,122,0.3)]' : ''}`}
          title={isPlaying ? "Остановить" : "Воспроизвести"}
        >
          {isPlaying ? (
              <Square className="w-3 h-3" fill="currentColor" />
          ) : (
              <Play className="w-3 h-3 ml-0.5" fill="currentColor" />
          )}
        </button>
        
        {/* Анимированный курсор/палец */}
        {isHintVisible && (
            <div 
                className={`absolute z-50 pointer-events-none transition-all duration-300 ease-out origin-top-left
                    ${isMobile ? 'top-[40%] left-[40%]' : 'top-[50%] left-[50%]'}
                    ${cursorState === 'hidden' ? 'opacity-0 translate-x-4 translate-y-4' : ''}
                    ${cursorState === 'visible' ? 'opacity-100 translate-x-0 translate-y-0' : ''}
                    ${cursorState === 'clicking' ? 'opacity-100 translate-x-0 translate-y-0 scale-75' : ''}
                `}
            >
                {isMobile ? (
                    <Pointer className="w-7 h-7 drop-shadow-lg -rotate-12" color="white" fill="#1f2937" strokeWidth={1.5} />
                ) : (
                    <MousePointer2 className="w-6 h-6 drop-shadow-lg" color="white" fill="#1f2937" strokeWidth={1.5} />
                )}
            </div>
        )}
      </div>
      
      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium cursor-pointer select-none" onClick={togglePlay}>
        {label}
      </span>
    </div>
  );
};

export default EditableAudio;